import { 
  Container, 
  Typography, 
  Grid, 
  Box,
  Divider,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import Hero from '../components/common/Hero';
import { Helmet } from 'react-helmet-async';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PollIcon from '@mui/icons-material/Poll';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { surveyService, generateSessionId } from '../services/api';

const Surveys = () => {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [completedSurveys, setCompletedSurveys] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSurveys = async () => {
      try {
        setLoading(true);
        const response = await surveyService.getActive();
        
        if (response && response.data) {
          setSurveys(response.data);
          
          // Check which surveys the user has already completed (for anonymous surveys only on list view)
          const sessionId = generateSessionId();
          const completedStatus = {};
          
          for (const survey of response.data) {
            if (!survey.allowMultipleResponses && survey.isAnonymous) {
              const result = await surveyService.checkExistingResponse(survey.id, sessionId, null, true);
              completedStatus[survey.id] = result.exists;
            }
            // For non-anonymous surveys, we check at submit time with their email
          }
          
          setCompletedSurveys(completedStatus);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching surveys:', err);
        setError('Failed to load surveys. Please try again later.');
        setLoading(false);
      }
    };

    fetchSurveys();
  }, []);

  const isExpired = (endDate) => {
    if (!endDate) return false;
    return new Date(endDate) < new Date();
  };

  const getTimeRemaining = (endDate) => {
    if (!endDate) return 'No deadline';
    
    const end = new Date(endDate);
    const now = new Date();
    const diff = end - now;
    
    if (diff < 0) return 'Expired';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days > 1) return `${days} days remaining`;
    if (days === 1) return '1 day remaining';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours > 1) return `${hours} hours remaining`;
    return 'Closing soon';
  };

  return (
    <>
      <Helmet>
        <title>Community Surveys | Brynleigh Estates HOA</title>
        <meta name="description" content="Participate in community surveys and help shape the future of Brynleigh Estates. Your voice matters in planning events and improving our neighborhood." />
        <link rel="canonical" href="https://brynleighestates.com/surveys" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://brynleighestates.com/surveys" />
        <meta property="og:title" content="Community Surveys | Brynleigh Estates HOA" />
        <meta property="og:description" content="Participate in community surveys and help shape the future of Brynleigh Estates. Your voice matters in planning events and improving our neighborhood." />
        <meta property="og:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Community Surveys | Brynleigh Estates HOA" />
        <meta name="twitter:description" content="Participate in community surveys and help shape the future of Brynleigh Estates." />
        <meta name="twitter:image" content="https://brynleighestates.com/images/brynleigh-estates-social-logo.jpeg" />
      </Helmet>
      
      <Hero 
        title="Community Surveys" 
        subtitle="Your voice matters - help us make our community better"
        imageUrl="/images/hills-bg.jpeg"
      />
      
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" gutterBottom align="center">
            Active Surveys
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 2, maxWidth: 600, mx: 'auto' }}>
            Help us understand the needs of our community by participating in these surveys. 
            Your feedback is valuable in planning events and improving our neighborhood.
          </Typography>
          <Divider sx={{ mb: 4 }} />
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ my: 4 }}>{error}</Alert>
          ) : surveys.length === 0 ? (
            <Alert severity="info" sx={{ my: 4 }}>
              There are no active surveys at this time. Check back later!
            </Alert>
          ) : (
            <Grid container spacing={3}>
              {surveys.map((survey, index) => (
                <Grid item xs={12} md={6} key={survey.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card 
                      elevation={2}
                      sx={{ 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 2,
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4
                        }
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PollIcon color="primary" sx={{ mr: 1 }} />
                            <Typography variant="h6" component="h3">
                              {survey.title}
                            </Typography>
                          </Box>
                          {survey.isAnonymous && (
                            <Chip label="Anonymous" size="small" color="secondary" variant="outlined" />
                          )}
                        </Box>
                        
                        {survey.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {survey.description.replace(/<[^>]*>/g, '').substring(0, 150)}
                            {survey.description.length > 150 ? '...' : ''}
                          </Typography>
                        )}
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 'auto' }}>
                          <AccessTimeIcon fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {getTimeRemaining(survey.endDate)}
                          </Typography>
                          {survey.questions && (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                              {survey.questions.length} question{survey.questions.length !== 1 ? 's' : ''}
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                      
                      <CardActions sx={{ p: 2, pt: 0 }}>
                        {completedSurveys[survey.id] ? (
                          <Button 
                            variant="outlined" 
                            color="success" 
                            fullWidth
                            disabled
                            startIcon={<CheckCircleIcon />}
                          >
                            Already Submitted
                          </Button>
                        ) : isExpired(survey.endDate) ? (
                          <Button 
                            variant="outlined" 
                            color="inherit" 
                            fullWidth
                            disabled
                          >
                            Survey Closed
                          </Button>
                        ) : (
                          <Button 
                            variant="contained" 
                            color="primary" 
                            fullWidth
                            onClick={() => navigate(`/surveys/${survey.id}`)}
                          >
                            Take Survey
                          </Button>
                        )}
                      </CardActions>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Container>
    </>
  );
};

export default Surveys;
